
import { useState } from 'react';
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/auth";

export const useProfileSubmission = (onComplete: (userData: {
  displayName: string;
  bio: string;
  knittingStyle: "continental" | "english" | "not-specified";
  measurementSystem: "metric" | "imperial";
  avatarUrl: string;
  emailMarketing: boolean;
  emailProjectUpdates: boolean;
  emailCommunityUpdates: boolean;
}) => void, onOpenChange: (open: boolean) => void, externalUserId?: string | null) => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const handleSubmit = async (formData: {
    displayName: string;
    bio: string;
    knittingStyle: "continental" | "english" | "not-specified";
    measurementSystem: "metric" | "imperial";
    avatarUrl: string;
    communicationPrefs: {
      email_marketing: boolean;
      email_project_updates: boolean;
      email_community_updates: boolean;
    };
  }) => {
    if (!formData.displayName) {
      toast({
        title: "Display name required",
        description: "Please enter a display name",
        variant: "destructive",
      });
      return;
    }
    
    // Use either the external user ID (from registration) or the current user's ID
    const userId = externalUserId || user?.id;
    
    if (userId) {
      setLoading(true);
      try {
        // Check if display name is unique
        const { data: existingUsers, error: checkError } = await supabase
          .from('profiles')
          .select('id')
          .eq('display_name', formData.displayName)
          .neq('id', userId);
          
        if (checkError) throw checkError;
        
        if (existingUsers && existingUsers.length > 0) {
          toast({
            title: "Display name already taken",
            description: "Please choose a different display name.",
            variant: "destructive",
          });
          setLoading(false);
          return;
        }
      
        console.log(`Updating profile for user ${userId}`);
        
        const { data, error } = await supabase
          .from('profiles')
          .update({
            display_name: formData.displayName,
            bio: formData.bio,
            knitting_style: formData.knittingStyle,
            measurement_system: formData.measurementSystem,
            avatar_url: formData.avatarUrl,
            email_marketing: formData.communicationPrefs.email_marketing,
            email_project_updates: formData.communicationPrefs.email_project_updates,
            email_community_updates: formData.communicationPrefs.email_community_updates,
            updated_at: new Date().toISOString()
          })
          .eq('id', userId)
          .select();
          
        if (error) {
          console.error("Error updating profile:", error);
          throw error;
        }
        
        console.log("Profile update result:", data);
        
        toast({
          title: "Profile updated",
          description: "Your profile has been successfully updated.",
        });
        
        onComplete({
          displayName: formData.displayName,
          bio: formData.bio,
          knittingStyle: formData.knittingStyle,
          measurementSystem: formData.measurementSystem,
          avatarUrl: formData.avatarUrl,
          emailMarketing: formData.communicationPrefs.email_marketing,
          emailProjectUpdates: formData.communicationPrefs.email_project_updates,
          emailCommunityUpdates: formData.communicationPrefs.email_community_updates,
        });
        
        onOpenChange(false);
        navigate("/account");
      } catch (error: any) {
        console.error("Profile update error:", error);
        toast({
          title: "Error updating profile",
          description: error.message || "Something went wrong. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    } else {
      // Handle the case where we don't have a user (e.g. in preview mode)
      console.log("No user ID available for profile update");
      onComplete({
        displayName: formData.displayName,
        bio: formData.bio,
        knittingStyle: formData.knittingStyle,
        measurementSystem: formData.measurementSystem,
        avatarUrl: formData.avatarUrl,
        emailMarketing: formData.communicationPrefs.email_marketing,
        emailProjectUpdates: formData.communicationPrefs.email_project_updates,
        emailCommunityUpdates: formData.communicationPrefs.email_community_updates,
      });
      
      onOpenChange(false);
      navigate("/account");
    }
  };
  
  const skipSetup = () => {
    console.log("Skipping onboarding setup");
    onComplete({
      displayName: "Knitting Enthusiast",
      bio: "I love creating custom knitting patterns!",
      knittingStyle: "not-specified",
      measurementSystem: "metric",
      avatarUrl: "https://images.unsplash.com/photo-1582562124811-c09040d0a901?w=200&h=200&fit=crop",
      emailMarketing: false,
      emailProjectUpdates: false,
      emailCommunityUpdates: false,
    });
    
    onOpenChange(false);
    navigate("/account");
  };
  
  return {
    handleSubmit,
    skipSetup,
    loading
  };
};
